import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { translations } from "@/i18n";
import { registerServiceWorker } from "@/push";
import { getDeviceFingerprint } from "@/lib/deviceId";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies for Emergent auth
axios.defaults.withCredentials = true;

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("chambanow_lang") || "en");
  // Whether the visitor explicitly picked a language (vs the "en" default). Lets
  // PR city pages default to Spanish while still honoring an explicit toggle.
  const [langExplicit, setLangExplicit] = useState(() => localStorage.getItem("chambanow_lang_explicit") === "1");
  const [token, setToken] = useState(() => localStorage.getItem("chambanow_token") || null);
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [unread, setUnread] = useState(0);
  // Market the visitor is browsing (set automatically by the city page they land on).
  // 'mx' shows MXN reference prices (charges still settle in USD). 'us' is the default.
  const [market, setMarket] = useState(() => localStorage.getItem("chambanow_market") || "us");
  const [usdToMxn, setUsdToMxn] = useState(18);
  const unreadTimer = useRef(null);
  const langInit = useRef(true);
  // Spanish dictionary is lazy-loaded (code-split) so English visitors never download
  // it. While it's loading on first ES switch, we fall back to English — brief and
  // only once, since dynamic UGC is already machine-translated with a notice.
  const [esBundle, setEsBundle] = useState(null);
  const [hideChrome, setHideChrome] = useState(false); // hide global Navbar/Footer (e.g. guided job wizard)
  useEffect(() => {
    if (lang === "es" && !esBundle) {
      import("@/i18n.es").then((m) => setEsBundle(m.es)).catch(() => {});
    }
  }, [lang, esBundle]);
  const t = lang === "es" ? (esBundle || translations.en) : translations.en;

  useEffect(() => { localStorage.setItem("chambanow_market", market); }, [market]);
  useEffect(() => {
    axios.get(`${API}/config/currency`).then(r => {
      if (r.data?.usd_to_mxn) setUsdToMxn(Number(r.data.usd_to_mxn));
    }).catch(() => {});
  }, []);

  // Format a USD price for display: MXN reference in the Mexico market, USD elsewhere.
  const fmtPrice = useCallback((usd) => {
    if (market === "mx") {
      const mxn = Math.round(Number(usd) * usdToMxn);
      return `$${mxn.toLocaleString("es-MX")} MXN`;
    }
    return `$${Number(usd).toFixed(2)}`;
  }, [market, usdToMxn]);

  useEffect(() => {
    localStorage.setItem("chambanow_lang", lang);
    // Skip the initial mount — only persist on change. The "applying your language"
    // notice is fired ONLY from toggleLang (an explicit user action), never on auto-
    // detect, saved-preference apply, or page navigation — so it's not noisy.
    if (langInit.current) {
      langInit.current = false;
      return;
    }
  }, [lang]);

  useEffect(() => {
    registerServiceWorker();
  }, []);

  const toggleLang = () => {
    localStorage.setItem("chambanow_lang_explicit", "1");
    setLangExplicit(true);
    setLang((l) => {
      const next = l === "en" ? "es" : "en";
      // Persist the choice so ALL emails (welcome, job alerts, nudges) match it.
      if (token) {
        axios.post(`${API}/auth/language`, { lang: next }, { headers: authHeaders() }).catch(() => {});
      }
      toast(next === "es"
        ? "Aplicando tu idioma: espera unos momentos mientras se traduce la página."
        : "Applying your language — please allow a few moments for the page to translate.",
        { id: "lang-translate-notice", duration: 2500 });
      return next;
    });
  };

  // Apply a signed-in user's saved language preference to the site.
  const applyUserLang = (u) => {
    const ul = (u?.lang || "").toLowerCase();
    if (ul === "en" || ul === "es") {
      localStorage.setItem("chambanow_lang", ul);
      localStorage.setItem("chambanow_lang_explicit", "1");
      setLangExplicit(true);
      setLang(ul);
    }
  };

  const authHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const fetchMe = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setLoadingUser(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
      setUser(r.data);
      applyUserLang(r.data);
      // Market follows the account: Mexican accounts → MX (Spanish + MXN), else US.
      // Prevents a stale market (from browsing a Mexican city page) sticking to a US user.
      setMarket((r.data?.country || "").toUpperCase() === "MX" ? "mx" : "us");
    } catch (e) {
      // A stale/invalid token must not leave a ghost "logged-in" shell (empty hirer
      // dashboard, Find Workers stuck loading). Clear it so the app returns to a clean
      // logged-out state.
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("chambanow_token");
        setToken(null);
      }
      setUser(null);
    } finally {
      setLoadingUser(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const refreshUnread = useCallback(async () => {
    if (!token) { setUnread(0); return; }
    try {
      const r = await axios.get(`${API}/chat/unread-count`, { headers: authHeaders() });
      setUnread(r.data.count || 0);
    } catch (e) {
      // ignore
    }
  }, [authHeaders, token]);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    refreshUnread();
    unreadTimer.current = setInterval(refreshUnread, 15000);
    return () => clearInterval(unreadTimer.current);
  }, [user, refreshUnread]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("chambanow_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    applyUserLang(r.data.user);
    setMarket((r.data.user?.country || "").toUpperCase() === "MX" ? "mx" : "us");
    return r.data.user;
  };

  const register = async (payload) => {
    const r = await axios.post(`${API}/auth/register`, { ...payload, device_fingerprint: getDeviceFingerprint() });
    localStorage.setItem("chambanow_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    applyUserLang(r.data.user);
    setMarket((r.data.user?.country || "").toUpperCase() === "MX" ? "mx" : "us");
    return r.data.user;
  };

  // Persist a Google/Emergent OAuth session the same way email login does — store
  // the session token as a bearer so auth survives return visits and cross-site
  // cookie blocking (Safari ITP etc.), not just the httpOnly cookie.
  const applySession = (sessionToken, userObj) => {
    if (sessionToken) {
      localStorage.setItem("chambanow_token", sessionToken);
      setToken(sessionToken);
    }
    if (userObj) setUser(userObj);
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch (e) {}
    localStorage.removeItem("chambanow_token");
    setToken(null);
    setUser(null);
    setUnread(0);
  };

  return (
    <AppContext.Provider value={{
      lang, t, toggleLang, setLang, langExplicit,
      market, setMarket, usdToMxn, fmtPrice,
      user, setUser, token, loadingUser,
      authHeaders, login, register, logout, fetchMe, applySession,
      unread, refreshUnread,
      hideChrome, setHideChrome,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
